import { useEffect } from "react"
import { Link, useParams, useSearchParams, useNavigate, useOutletContext } from "react-router-dom"
import { useComputed, useSignal, useSignalEffect } from "@preact/signals-react"
import _ from "lodash-es"

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Button from "react-bootstrap/esm/Button.js"
import Dropdown from 'react-bootstrap/Dropdown'
import DropdownButton from 'react-bootstrap/DropdownButton'
import Form from 'react-bootstrap/Form'
import Nav from 'react-bootstrap/Nav'
import Pagination from 'react-bootstrap/Pagination'
import Spinner from 'react-bootstrap/Spinner'
import Stack from 'react-bootstrap/Stack'
import Table from 'react-bootstrap/Table'

import DebugAccordion from "../../components/DebugAccordion"
import DevicesListFilteringModal from "./components/DevicesListFilteringModal.jsx"
import DeviceKeysModal from "./components/DeviceKeysModal"
import Icon from "../../components/Icon"
import PageTitle from "../../components/PageTitle.jsx"
import ConfirmationModal from "../../components/ConfirmationModal"
import { NotificationTypes, addNotification } from "../../stores/notification-store.js"
import IncompatibleDevicesModal from "./components/IncompatibleDevicesModal"
import PushFormsModal from "./components/PushFormsModal"
import TimestampWithUTCTooltip from "../../components/TimestampWithUTCTooltip"

import { saveFile, parseString } from '../../utils/utils'
import { ExportDeviceList } from '../../utils/api'
import { CreateTaskTypeEnum, SortDirectionType } from "../../utils/constants.js"

import { deviceListSignal as devicesSignal, deviceFormsSignal, deviceListLoadingSignal as loadingSignal } from "../../state/signals/deviceSignals.js"
import { fetchDeviceList, fetchDeviceForms } from "../../state/actions/deviceActions.js"
import { pageSizeSignal as rowsPerPageSignal, debugSignal, deviceListFiltersValueSignal as filtersValueSignal, deviceListFiltersCountSignal, deviceListFiltersValueSignal, selectedBusinessEntityIdSignal } from "../../state/signals/userSettingsSignals.js"
import { updatePageSize, updateDeviceListFilter, removeDeviceListFilter } from "../../state/actions/userSettingsActions.js"
import { InputGroup } from "react-bootstrap"


function Devices() {

	const navigate = useNavigate()
	const { beId } = useParams()
	const [searchParams] = useSearchParams()
    const { user, config, accessToken, userPermissions, userPermissionsSummary, accordionData: sidebarAccordionData  } = useOutletContext()
	
	useEffect(() => {
		if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewDevices) {
            addNotification('You are not authorized to view UTG devices for this account.', NotificationTypes.ERROR, true)
            navigate(`/`)
        }
    }, [userPermissionsSummary.value])

    const canEditSignal = useComputed(() => {
        return userPermissionsSummary?.value?.canEditDevices
    })

	const currentPageSignal = useSignal(1)
	const devicePciVersionSignal = useSignal(null)
	const filterStringSignal = useSignal("")
	const isSelectAllSignal = useSignal(false)
	const selectedRecordSignal = useSignal(null)

	const initializeSelectedRows = function () {
		let dict = {}
		for (let i in devicesSignal.value) {
			dict[''.concat(devicesSignal.value[i].deviceConfigId)] = false
		}
		return dict
	}

	const selectedRowsSignal = useSignal(initializeSelectedRows())
	const selectedDevicesSignal = useSignal([])
	const showModalDeviceKeysSignal = useSignal(false)
	const showModalIncompatibleDevicesSignal = useSignal(false)
	const showModalPushFormsSignal = useSignal(false)
	const sortColumnSignal = useSignal('')
	const taskTypeSignal = useSignal(0)
	const selectedRowsCountLabelSignal = useSignal(0)

	//Export Devices as csv
	const showDownloadProgressSignal = useSignal(false)
	const downloadProgressMessageSignal = useSignal('')
	const showExportConfirmationModalSignal = useSignal(false)

	const sortDirectionSignal = useSignal(SortDirectionType.DESCENDING)

	useEffect(() => {
		if (selectedBusinessEntityIdSignal.value !== beId) {
			removeDeviceListFilter()
		}
		fetchDeviceList(beId, filtersValueSignal.value, config, accessToken)
		fetchDeviceForms(beId, config, accessToken)
	}, [beId])

	const deviceIncompatibilitySignal = useSignal(false)
	const pciIncompatibilitySignal = useSignal(false)
	const utgIncompatibilitySignal = useSignal(false)

	const filteredDataSignal = useComputed(() => {
		try {
			let filtered;
			if (!filterStringSignal.value) {
				filtered = devicesSignal.value ? [...devicesSignal.value] : []
			}
			else {
				// Filter the data based on the filter text
				// Convert the filter to lowercase
				const lowerCaseFilter = filterStringSignal.value.toLowerCase()
				// Filter the data based on the filter text
				filtered = devicesSignal.value.filter(item => {
					// Limit search to these fields
					let searchData = {
						ComponentMerchant: item.componentMerchant,
						ComponentNickname: item.componentNickname,
						UtgStatus: item.utgStatus,
						UtgVersion: item.utgVersion,
						AgentVersion: item.agentVersion,
						ConfigStatus: item.configStatus,
						ComponentProfileName: item.componentProfileName,
						ComponentProfileVersion: item.componentProfileVersion,
						ComponentLocation: item.componentLocation,
						ComponentLane: item.componentLane,
						ComponentFileId: item.componentFileId,
						MachineDnsName: item.machineDnsName,
						MachineStatus: item.machineStatus,
						MachineStatusEntityLastKnownCommunication: item.machineStatusEntityLastKnownCommunication,
						MachineStatusEntityNextPredictedCommunication: item.machineStatusEntityNextPredictedCommunication,
						MachineNickname: item.machineNickname,
						BusinessEntityId: item.businessEntityId,
						StandardTimeZone: item.machineStatusEntityStandardTimeZone,
						WindowsVersion: item.machineStatusEntityWindowsVersion,

						ComponentPermanentId: item.componentPermanentId,
						MachinePermanentId: item.machinePermanentId,

						DeviceConfigApiTerminalId: item.deviceConfigApiTerminalId,
						DeviceConfigDeviceName: item.deviceConfigDeviceName,
						DeviceConfigDeviceType: item.deviceConfigDeviceType,
						DeviceStatusDevicePciVersion: item.deviceStatusDevicePciVersion,
						DeviceStatusDeviceSerialNumber: item.deviceStatusDeviceSerialNumber,
						DeviceStatusRBAVersion: item.deviceStatusRBAVersion
					}
					return Object.values(searchData).some(value => {
						if (value && (typeof (value) == 'string')) {
							return value.toString().toLowerCase().includes(lowerCaseFilter)
						}
					})
				})
			}
			return filtered
		} catch (x) {
			console.trace(x.toString())
			window.alert(x.toString())
		}
	})

	const sortedDataSignal = useComputed(() => {

		// just return filtered data if no sorting
		if (!sortColumnSignal.value.length) {
			return [...filteredDataSignal.value]
		}

		let _sortedData = [...filteredDataSignal.value]
		return _sortedData.sort((a, b) => {
			let _a = _.get(a, sortColumnSignal.value, null)
			let _b = _.get(b, sortColumnSignal.value, null)

			// Convert nulls to empty strings for sorting purposes
			_a = _a ? _a : ''
			_b = _b ? _b : ''

			// convert values to lowercase
			if (_a && _a.length) {
				_a = _a.toLowerCase()
			}
			if (_b && _b.length) {
				_b = _b.toLowerCase()
			}

			if (sortDirectionSignal.value == SortDirectionType.DESCENDING) {
				if (_a < _b) return -1
				if (_a > _b) return 1
				return 0
			}
			else {
				if (_a > _b) return -1
				if (_a < _b) return 1
				return 0
			}
		})
		//return _sortedData
	})

	const totalPagesSignal = useComputed(() => {
		return Math.ceil(filteredDataSignal.value.length / parseInt(rowsPerPageSignal.value, 10))
	})

	const displayDataSignal = useComputed(() => {
		// Calculate the starting and ending indexes for pagination
		const startIndex = (currentPageSignal.value - 1) * rowsPerPageSignal.value
		const endIndex = startIndex + rowsPerPageSignal.value
		const display = sortedDataSignal.value.slice(startIndex, endIndex)
		return display
	})
	const startDisplayRowNumberSignal = useComputed(() => {
		return ((currentPageSignal.value - 1) * rowsPerPageSignal.value) + 1
	})
	const endDisplayRowNumberSignal = useComputed(() => {
		const startIndex = (currentPageSignal.value - 1) * rowsPerPageSignal.value
		const endIndex = startIndex + rowsPerPageSignal.value
		return endIndex < filteredDataSignal.value.length ? endIndex : filteredDataSignal.value.length
	})

	const accordionData = [
		// { name: 'Properties', data: { Count: devicesSignal.value.length, 'Filter Count': filteredDataSignal.value.length, 'Display Count': displayDataSignal.value.length, Filter: filterStringSignal.value, CurrentPage: currentPageSignal.value, RowsPerPage: rowsPerPageSignal, accessToken: accessToken } },
		{ name: 'Filtered Data', data: filteredDataSignal.value },
		{ name: 'Data', data: devicesSignal.value },
		{ name: 'Display Data', data: displayDataSignal.value },
		{ name: 'Selected Rows', data: selectedRowsSignal.value },
		{ name: 'Access Token', data: accessToken },
		{ name: 'App Config', data: config }
	]

	const handlePageSizeSelect = (v) => {
		currentPageSignal.value = 1		
        updatePageSize(parseInt(v, 10))

		let count = 0
		let display_us = new Set()
		for (let i in displayDataSignal.value) {
			let dcid = ''.concat(displayDataSignal.value[i].deviceConfigId)
			display_us.add(dcid)
			if (!(dcid in selectedRowsSignal.value)) {
				selectedRowsSignal.value[dcid] = false
			}
		}
		for (let i in selectedRowsSignal.value) {
			if (selectedRowsSignal.value[i] && display_us.has(i)) {
				selectedRowsSignal.value[i] = true
				count++;
			}
			else {
				selectedRowsSignal.value[i] = false
			}
		}
		selectedRowsCountLabelSignal.value = count
	}
	const handleFilterStringChange = (e) => {
		currentPageSignal.value = 1
		filterStringSignal.value = e.currentTarget.value
	}

	const handleCopyClipboard = item => {
		if (item) {
			// place the data on the users clipboard if the browser supports it
			if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
				navigator.clipboard.writeText(JSON.stringify(item, null, 2));
				addNotification(`The data for ${item.componentNickname} has been copied to your clipboard.`, NotificationTypes.SUCCESS, false, 'Copy Succeeded')
			}
			else {
				addNotification('This browser does not support copying data to the clipboard.', NotificationTypes.INFO, false, 'Copy Failed')
			}
		}
	}

	const showPushFormsModal = function (item, task_type) {
		try {
			if (!selectedRowsSignal.value[item.deviceConfigId]) {
				handleSelectRow(item)
			}
			selectedDevicesSignal.value = []
			for (let i in displayDataSignal.value) {
				let item = displayDataSignal.value[i]
				if (item.deviceStatusDeviceType != null) {
					let dcid = ''.concat(item.deviceConfigId)
					if (selectedRowsSignal.value[dcid]) {
						selectedDevicesSignal.value.push(item)
					}
				}
			}
			if (selectedDevicesSignal.value.length == 0) {
				window.alert("No devices selected")
				return
			}
			const deviceStatusDeviceTypes = selectedDevicesSignal.value.map(device => device.deviceStatusDeviceType)
			let uniqueDeviceTypes = deviceStatusDeviceTypes.filter((item, i, arr) => arr.indexOf(item) === i)
			if (uniqueDeviceTypes.length == 1) {
				if (''.concat(uniqueDeviceTypes[0]).length == 0) {
					deviceIncompatibilitySignal.value = true
				} else {
					deviceIncompatibilitySignal.value = false
				}
			} else {
				deviceIncompatibilitySignal.value = true
			}
			utgIncompatibilitySignal.value = selectedDevicesSignal.value.some(function (device) {
				const softwareVersion = parseInt(device.utgVersion.split(".")[3]);
				if (softwareVersion < 3018) {
					return true;
				}
			})
			const devicePciVersions = selectedDevicesSignal.value.map(record => record.deviceStatusDevicePciVersion)
			let uniquePciVersions = devicePciVersions.filter((item, i, ar) => ar.indexOf(item) === i);
			if (uniquePciVersions.length == 1) {
				pciIncompatibilitySignal.value = false
			} else {
				pciIncompatibilitySignal.value = true
			}
			if (deviceIncompatibilitySignal.value || utgIncompatibilitySignal.value || pciIncompatibilitySignal.value) {
				showIncompatibleDevicesModal()
			} else {
				taskTypeSignal.value = task_type
				devicePciVersionSignal.value = devicePciVersions[0]
				devicePciVersionSignal.value = item.deviceStatusDevicePciVersion
				showModalPushFormsSignal.value = true
			}
		}
		catch (x) {
			console.trace()
			console.error("showPushFormsModal exception: ".concat(x.toString()))
			window.alert("showPushFormsModal exception: ".concat(x.toString()))
		}
	}

	const showIncompatibleDevicesModal = function () {
		showModalIncompatibleDevicesSignal.value = true
	}

	// Devices Filtering Modal
	const showDevicesFilteringModalSignal = useSignal(false)
	const handleCancelDevicesFiltering = () => showDevicesFilteringModalSignal.value = false
	const handleApplyDevicesFilters = (predicates) => {
		updateDeviceListFilter(predicates)
		fetchDeviceList(beId, filtersValueSignal.value, config, accessToken)
		showDevicesFilteringModalSignal.value = false
	}
	const handleClearFilter = () => {
		removeDeviceListFilter()
		fetchDeviceList(beId, deviceListFiltersValueSignal.value, config, accessToken)
	}

	const handleSelectAll = () => {
		try {
			if (isSelectAllSignal.value) {
				// deselect all
				for (let i in selectedRowsSignal.value) {
					selectedRowsSignal.value[i] = false
				}
				selectedRowsCountLabelSignal.value = 0
				isSelectAllSignal.value = false
				return
			}
			// select all rows from filtered data
			isSelectAllSignal.value = true
			let count = 0
			for (let i in displayDataSignal.value) {
				let item = displayDataSignal.value[i]
				if (item.deviceStatusDeviceType != null) {
					selectedRowsSignal.value[''.concat(item.deviceConfigId)] = true
					count++
				}
			}
			selectedRowsCountLabelSignal.value = count //displayDataSignal.value.length
		}
		catch (x) {
			console.trace(x.toString())
			window.alert(x.toString())
		}
	}

	const handleSelectRow = (item) => {
		const dcid = ''.concat(item.deviceConfigId)
		selectedRowsSignal.value[dcid] = !(selectedRowsSignal.value[dcid])
		if (selectedRowsSignal.value[dcid]) {
			selectedRowsCountLabelSignal.value++
		}
		else {
			selectedRowsCountLabelSignal.value--
		}
	}

	const handleHeaderTableClick = datafield => {
		// if already sorted on this column then toggle sort direction
		if (datafield == sortColumnSignal.value) {
			console.log("Toggling sort direction of field ".concat(datafield))
			sortDirectionSignal.value = sortDirectionSignal.value == SortDirectionType.DESCENDING ? SortDirectionType.ASCENDING : SortDirectionType.DESCENDING
			return
		}

		// update sort column - the computed functions will then run and render the sorted data
		sortColumnSignal.value = datafield
		console.log("Updated sort column to ".concat(datafield))
	}

	function TableRows({ items }) {
		if (items && items.length) {
			let keys = new Set()
			const rows = items.map(item => {
				let key = item.deviceConfigId
				if (keys.has(key)) {
					return
				}
				keys.add(key)
				return <TableRow item={item} key={`${key}`} />
			})

			return (
				<tbody>{rows}</tbody>
			)
		}
		else if (items) {
			return (
				<tbody><tr><td colSpan="27" className="text-center">This business entity does not contain any devices</td></tr></tbody>
			)
		}
	}

	const handleDeviceKeysClicked = function (item) {
		selectedRecordSignal.value = item
		showModalDeviceKeysSignal.value = true
	}

	function TableRow({ item }) {
		let has_device_keys = item.deviceStatusDeviceKey0 != null
			|| item.deviceStatusDeviceKey1 != null
			|| item.deviceStatusDeviceKey2 != null
			|| item.deviceStatusDeviceKey3 != null
			|| item.deviceStatusDeviceKey4 != null
			|| item.deviceStatusDeviceKey5 != null
		return (
			<tr>
				<td>
					<Form.Check hidden={item.deviceStatusDeviceType == null} aria-label="Select Row" checked={selectedRowsSignal.value[''.concat(item.deviceConfigId)]} onChange={() => handleSelectRow(item)} />
				</td>
				<td>
					<Dropdown>
						<Dropdown.Toggle variant="link" size="sm" id="actions"><Icon icon="gear" width="16" height="16"></Icon></Dropdown.Toggle>
						<Dropdown.Menu>
							<Dropdown.Item onClick={() => handleCopyClipboard(item)}><Icon icon="copy" width="16" height="16"></Icon>&nbsp;Copy Row as JSON</Dropdown.Item>
							{canEditSignal.value && <Dropdown.Item hidden={item.deviceStatusDeviceType == null} onClick={() => showPushFormsModal(item, CreateTaskTypeEnum.UpdateDeviceForms)}><Icon icon="arrow-circle-o-up" width="16" height="16"></Icon>&nbsp;Push forms</Dropdown.Item>}
							{canEditSignal.value && <Dropdown.Item hidden={item.deviceStatusDeviceType == null} onClick={() => showPushFormsModal(item, CreateTaskTypeEnum.UpdateDeviceFirmware)}><Icon icon="refresh" width="16" height="16"></Icon>&nbsp;Update Firmware</Dropdown.Item>}
						</Dropdown.Menu>
					</Dropdown>
				</td>
				<td>{item.componentMerchant}</td>
				<td><Link to={`../../be/${item.businessEntityId}/utgs/${item.componentId}`}>{item.componentNickname}</Link></td>
				<td>{item.deviceStatusDeviceSerialNumber}</td>
				<td>{item.deviceConfigDeviceType}</td>
				<td>{item.deviceStatusDeviceType}</td>
				<td>{item.deviceConfigApiTerminalId}</td>
				<td>{item.deviceStatusDevicePciVersion}</td>
				<td>{item.deviceStatusFirmwareVersion}</td>
				{/*<td>{item.deviceStatusRBAVersion}</td>*/}
				<td>
					<a hidden={!has_device_keys} style={{ cursor: 'pointer' }} onClick={() => handleDeviceKeysClicked(item)}>...</a>
					<span hidden={has_device_keys}>N/A</span>
				</td>
				<td>{item.machineDnsName}</td>
				<td>{item.utgStatus}</td>
				<td>{item.utgVersion}</td>
				<td>{item.agentVersion}</td>
				<td>{item.configStatus}</td>
				<td>{item.componentProfileName}</td>
				<td>{item.componentProfileVersion}</td>
				<td>{item.componentLocation}</td>
				<td>{item.componentLane}</td>
				<td>{item.componentFileId}</td>
				<td>{item.machineStatus}</td>
				<td><TimestampWithUTCTooltip date={item.machineStatusEntityLastKnownCommunication}></TimestampWithUTCTooltip></td>
				<td><TimestampWithUTCTooltip date={item.machineStatusEntityNextPredictedCommunication}></TimestampWithUTCTooltip></td>
				<td>{item.machineNickname}</td>
				<td>{item.businessEntityId}</td>
                <td>{item.machineStatusEntityStandardTimeZone}</td>
                <td>{item.machineStatusEntityWindowsVersion}</td>
			</tr>
		)
	}

	const setCurrentPage = function (page_num) {
		if (currentPageSignal.value != page_num) {
			currentPageSignal.value = page_num
			for (let i in selectedRowsSignal.value) {
				selectedRowsSignal.value[i] = false
			}
			selectedRowsCountLabelSignal.value = 0
			isSelectAllSignal.value = false
		}
	}

	function PaginationItems() {
		let items = []
		for (let i = 1; i <= totalPagesSignal.value; i++) {
			items.push(
				<Pagination.Item key={i} active={i === currentPageSignal.value} onClick={() => { setCurrentPage(i) }}>{i}</Pagination.Item>
			)
		}
		return items
	}

	function TableColumnHeader({ title, sortable, datafield }) {
		// non-sortable columns do not have click handlers
		if (!sortable) {
			return (
				<th>{title}</th>
			)
		}

		// sortable column but isn't currently sorted on this column. Add click handler but no caret indicators
		if (datafield != sortColumnSignal.value) {
			return (
				<th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}</th>
			)
		}

		// datafield matches sortColumn signal so we need to add a caret icon. sortDirection signal determines the direction
		if (sortDirectionSignal.value == SortDirectionType.DESCENDING) {
			return (
				<th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
					<Icon icon="caret-down" width="16" height="16"></Icon>
				</th>
			)
		}

		// else must be ascending
		return (
			<th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
				<Icon icon="caret-up" width="16" height="16"></Icon>
			</th>
		)
	}

	const handleCloseModalDeviceKeys = () => {
		showModalDeviceKeysSignal.value = false
		selectedRecordSignal.value = null

	}

	const handleCloseModalIncompatibleDevices = () => {
		console.log("Hiding the incompatible devices modal")
		showModalIncompatibleDevicesSignal.value = false
	}

	const handleCloseModalPushForms = () => {
		console.log("handleCloseModalPushForms")
		showModalPushFormsSignal.value = false
	}

	const exportDevices = async () => {
		// set showDownloadProgress to true to show spinner
		showDownloadProgressSignal.value = true
		downloadProgressMessageSignal.value = 'Preparing to export device list'
		try {
			const exportDeviceListResponse = await ExportDeviceList(beId, config, accessToken)
			saveFile(exportDeviceListResponse, "devices.csv")
			addNotification('Exporting device list. Your download should begin shortly.', NotificationTypes.SUCCESS, true)
			showDownloadProgressSignal.value = false
		}
		catch (error) {
			console.log(error)
			addNotification(`Unable to export device list. ${error}`, NotificationTypes.ERROR, true)
			showDownloadProgressSignal.value = false
		}
	}

	function DownloadProgress() {
		if (showDownloadProgressSignal.value) {
			return (
				<div className="d-flex flex-column align-items-center justify-content-center">
					<Spinner className="mt-5" animation="border" role="status">
						<span className="visually-hidden">{downloadProgressMessageSignal.value}</span>
					</Spinner>
					<p className="mt-3">{downloadProgressMessageSignal.value}</p>
				</div>
			)
		}
	}

	if (loadingSignal.value || !devicesSignal.value) {
		return (
			<div className="d-flex justify-content-center">
				<Spinner className="mt-5 align-center" animation="border" role="status">
					<span className="visually-hidden">Loading...</span>
				</Spinner>
			</div>
		)
	}

	try {
		return (
			<>
				<DownloadProgress></DownloadProgress>
				<ConfirmationModal show={showExportConfirmationModalSignal.value} handleCancel={() => showExportConfirmationModalSignal.value = false} handleConfirm={() => { showExportConfirmationModalSignal.value = false; exportDevices(); }} message={'Export may take a few minutes to download ALL devices on this tab. Are you sure you want to continue?'} title={"Export device list"} />
				<Breadcrumb className="px-3 pt-1 bg-light">
					<Breadcrumb.Item active>Device Management</Breadcrumb.Item>
				</Breadcrumb>
				<PageTitle title='Device Management' canEditSignal={canEditSignal} />
				<Nav className="justify-content-between px-3 py-1 my-2 bg-light">
					<Stack direction="horizontal" gap={3} className="me-auto">
						<Nav.Item>
							<InputGroup>
								<Button size="md" variant="outline-secondary" onClick={() => handleClearFilter()}><Icon icon='trash' width="16" height="16"></Icon></Button>
								<Button size="md" variant="outline-secondary" onClick={() => showDevicesFilteringModalSignal.value = true}><Icon icon='funnel' width="16" height="16"></Icon>&nbsp;Filters ({deviceListFiltersCountSignal.value})</Button>
							</InputGroup>
						</Nav.Item>
						{/*<FilterButton variant="outline-primary" showSignal={showModalFiltersSignal} filterCountSignal={filterCountSignal}></FilterButton>*/}
						<DropdownButton variant="outline-secondary" title={`${rowsPerPageSignal.value} records per page`} id="pageSize-dropdown" onSelect={handlePageSizeSelect}>
							<Dropdown.Item eventKey="10">10</Dropdown.Item>
							<Dropdown.Item eventKey="25">25</Dropdown.Item>
							<Dropdown.Item eventKey="50">50</Dropdown.Item>
							<Dropdown.Item eventKey="250">250</Dropdown.Item>
							<Dropdown.Item eventKey="500">500</Dropdown.Item>
							<Dropdown.Item eventKey="1000">1000</Dropdown.Item>
						</DropdownButton>
					</Stack>
					<Nav.Item className="mt-1">
						<Form.Control type="text" placeholder="Search" size="sm" htmlSize="75" checked={isSelectAllSignal.value} onChange={handleFilterStringChange} />
					</Nav.Item>

					<Nav.Item className="mt-1 ms-2">
						<Button size="sm" variant="outline-secondary" onClick={exportDevices}><Icon icon='download' width="16" height="16"></Icon>&nbsp;Download data as CSV</Button>
					</Nav.Item>
				</Nav>
				<Table responsive striped hover size="sm" className="cc-table">
					<thead>
						<tr>
							<td><Form.Check type="checkbox" id="select-all-switch" aria-label="Select All" label={selectedRowsCountLabelSignal.value} checked={isSelectAllSignal.value} onChange={() => handleSelectAll()} /></td>
							<TableColumnHeader title={'Action'} sortable={false}></TableColumnHeader>
							<TableColumnHeader title={'Merchant'} sortable={true} datafield={'componentMerchant'}></TableColumnHeader>
							<TableColumnHeader title={'UTG Nickname'} sortable={true} datafield={'componentNickname'}></TableColumnHeader>
							<TableColumnHeader title={'Device Serial'} sortable={true} datafield={'deviceStatusDeviceSerialNumber'}></TableColumnHeader>
							<TableColumnHeader title={'Device Configured'} sortable={true} datafield={'deviceConfigDeviceType'}></TableColumnHeader>
							<TableColumnHeader title={'Device Reported'} sortable={true} datafield={'deviceStatusDeviceType'}></TableColumnHeader>
							<TableColumnHeader title={'Terminal ID'} sortable={true} datafield={'deviceConfigApiTerminalId'}></TableColumnHeader>
							<TableColumnHeader title={'PCI Version'} sortable={true} datafield={'deviceStatusDevicePciVersion'}></TableColumnHeader>
							<TableColumnHeader title={'Firmware Version'} sortable={true} datafield={'deviceStatusFirmwareVersion'}></TableColumnHeader>
							{/*<TableColumnHeader title={'RBA Version'} sortable={true} datafield={'deviceStatusRBAVersion'}></TableColumnHeader>*/}
							<TableColumnHeader title={'Device Keys'} sortable={false}></TableColumnHeader>
							<TableColumnHeader title={'Machine DNS Name'} sortable={true} datafield={'machineDnsName'}></TableColumnHeader>
							<TableColumnHeader title={'UTG Status'} sortable={true} datafield={'utgStatus'}></TableColumnHeader>
							<TableColumnHeader title={'UTG Version'} sortable={true} datafield={'utgVersion'}></TableColumnHeader>
							<TableColumnHeader title={'Agent Version'} sortable={true} datafield={'agentVersion'}></TableColumnHeader>
							<TableColumnHeader title={'Configuration Status'} sortable={true} datafield={'configStatus'}></TableColumnHeader>
							<TableColumnHeader title={'Profile'} sortable={true} datafield={'componentProfileName'}></TableColumnHeader>
							<TableColumnHeader title={'Profile Version'} sortable={true} datafield={'componentProfileVersion'}></TableColumnHeader>
							<TableColumnHeader title={'Location/Serial'} sortable={true} datafield={'componentLocation'}></TableColumnHeader>
							<TableColumnHeader title={'Lane'} sortable={true} datafield={'componentLane'}></TableColumnHeader>
							<TableColumnHeader title={'File ID'} sortable={true} datafield={'componentFileId'}></TableColumnHeader>
							<TableColumnHeader title={'Machine Status'} sortable={true} datafield={'machineStatus'}></TableColumnHeader>
							<TableColumnHeader title={'Last Known Communication'} sortable={true} datafield={'machineStatusEntityLastKnownCommunication'}></TableColumnHeader>
							<TableColumnHeader title={'Next Predicted Communication'} sortable={true} datafield={'machineStatusEntityNextPredictedCommunication'}></TableColumnHeader>
							<TableColumnHeader title={'Machine Nickname'} sortable={true} datafield={'machineNickname'}></TableColumnHeader>
							<TableColumnHeader title={'Parent Account'} sortable={true} datafield={'businessEntityId'}></TableColumnHeader>
							<TableColumnHeader title={'Time Zone'} sortable={true} datafield={'machineStatusEntityStandardTimeZone'}></TableColumnHeader>
							<TableColumnHeader title={'Windows Version'} sortable={true} datafield={'machineStatusEntityWindowsVersion'}></TableColumnHeader>
						</tr>
					</thead>
					<TableRows items={displayDataSignal.value} />
					{displayDataSignal.value.length > 0 && <tfoot><tr><td className="fs-6" colSpan={27}>Displaying {startDisplayRowNumberSignal.value}-{endDisplayRowNumberSignal.value} of {filteredDataSignal.value.length}</td></tr></tfoot>}
				</Table>
				<Pagination className="px-3 d-flex flex-wrap" size="sm"><PaginationItems /></Pagination>
				<DebugAccordion data={accordionData} debug={debugSignal} />

				<IncompatibleDevicesModal
					showSignal={showModalIncompatibleDevicesSignal}
					closeModal={handleCloseModalIncompatibleDevices}
					deviceIncompatibilitySignal={deviceIncompatibilitySignal}
					utgIncompatibilitySignal={utgIncompatibilitySignal}
					pciIncompatibilitySignal={pciIncompatibilitySignal}
				></IncompatibleDevicesModal>

				<PushFormsModal
					showSignal={showModalPushFormsSignal}
					handleCancel={handleCloseModalPushForms}
					taskType={taskTypeSignal.value}
					deviceFormsSignal={deviceFormsSignal}
					selectedDevices={selectedDevicesSignal.value}
					devicePciVersionSignal={devicePciVersionSignal}
				></PushFormsModal>

				<DeviceKeysModal
					show={showModalDeviceKeysSignal.value}
					handleClose={handleCloseModalDeviceKeys}
					selectedRecord={selectedRecordSignal.value}
				></DeviceKeysModal>

				<DevicesListFilteringModal
					show={showDevicesFilteringModalSignal.value}
					handleCancel={handleCancelDevicesFiltering}
					handleSubmit={handleApplyDevicesFilters}
				></DevicesListFilteringModal>
			</>
		)
	}
	catch (x) {
		console.trace(x.toString())
		window.alert(x.toString())
	}
}

export default Devices
